"use client";
import { memo, useEffect, useRef } from "react";
import { TrackReferenceOrPlaceholder } from "@livekit/components-react";
import { RemoteTrackPublication, LocalTrackPublication } from "livekit-client";

interface VideoPipFallbackProps {
  track: TrackReferenceOrPlaceholder | null; /** The primary track to show in PiP (screen share or active speaker) */

  onVideoElementReady: (element: HTMLVideoElement | null) => void; /** Callback to register the video element for PiP */
}

/**
 * VideoPipFallback - Hidden video element for Firefox/Safari PiP fallback.
 * This component renders a hidden video that can be used with the video element PiP API.
 */
export const VideoPipFallback = memo(function VideoPipFallback({ track, onVideoElementReady }: VideoPipFallbackProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Register the video element when it mounts
  useEffect(() => {
    onVideoElementReady(videoRef.current);
    return () => onVideoElementReady(null);
  }, [onVideoElementReady]);

  // Attach the track to the video element
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !track) return;

    const publication = track.publication as RemoteTrackPublication | LocalTrackPublication | undefined;
    const mediaTrack = publication?.track;

    if (mediaTrack) {
      mediaTrack.attach(videoElement);
      return () => {
        mediaTrack.detach(videoElement);
      };
    }
  }, [track]);

  // Don't render anything if no track
  if (!track) return null;

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted // Must be muted to allow autoplay
      className="hidden" // Hidden - only used for PiP API
      aria-hidden="true"
    />
  );
});
